import React from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  Header,
  Modal,
  SpaceBetween,
  TextContent,
} from "@amzn/awsui-components-react";
import {
  InvestigationOverlap,
  InvestigationPreview,
  Stage,
} from "../../lusca-search-api/generated-src";
import { InvestigationStatusChip } from "./InvestigationStatusChip";
import { OpenInvestigationButton } from "./OpenInvestigationButton";
import { relationLabel, withoutCancelled } from "./PreviewOverlaps";
import { formatTimestamp, ownerLabel } from "./investigationUtils";

export type OverlapChoice =
  | { kind: "remainder" }
  | { kind: "child"; parentInvestigationId: string }
  | { kind: "full" };

interface InvestigationOverlapDialogProps {
  visible: boolean;
  stage: Stage;
  preview: InvestigationPreview;
  onDismiss: () => void;
  onChoose: (choice: OverlapChoice) => void;
}

/** Fixed width so the action column lines up across every overlap card. */
const ACTION_COLUMN_WIDTH = "220px";

/**
 * Offered when a proposed population overlaps active investigations: open the existing case (in a
 * new tab, so the builder is not lost), create for the unassigned remainder only, or create a
 * deliberately narrower child.
 */
export const InvestigationOverlapDialog: React.FC<
  InvestigationOverlapDialogProps
> = ({ visible, stage, preview, onDismiss, onChoose }) => {
  const active = withoutCancelled(preview.activeOverlaps);
  const history = withoutCancelled(preview.historicalOverlaps);
  const remainder = preview.unassignedCount ?? 0;
  const assigned = preview.assignedCount ?? 0;
  const total = preview.count ?? 0;
  const canBeChild = (overlap: InvestigationOverlap) =>
    overlap.relation === "rule_subset";
  const anyChild = active.some(canBeChild);

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header={
        <Header variant="h2">
          This population overlaps active investigations
        </Header>
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => onChoose({ kind: "remainder" })}
              disabled={remainder === 0}
            >
              Create for unassigned remainder ({remainder})
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Alert type="warning">
          {assigned > 0
            ? `${assigned} of ${total} matching items are already owned by an active investigation.`
            : `None of the ${total} matching items are owned yet, but ${
                active.length
              } active investigation${
                active.length === 1 ? " uses" : "s use"
              } an overlapping rule.`}
          {preview.itemOverlapSampled &&
            " Overlap was estimated from a sample."}
        </Alert>

        <TextContent>
          <ul>
            <li>
              <strong>Open investigation</strong> – opens the existing
              investigation in a new tab. Nothing is created; this builder stays
              as it is.
            </li>
            {anyChild && (
              <li>
                <strong>Split out a narrower case</strong> – your rule is
                strictly narrower than that investigation&apos;s. The new case
                takes over its subset; the original keeps the remaining items
                and stays linked.
              </li>
            )}
            <li>
              <strong>Create for unassigned remainder</strong> – creates a new
              investigation for only the {remainder} item
              {remainder === 1 ? "" : "s"} no active investigation owns. Shared
              items stay where they are.
            </li>
          </ul>
        </TextContent>

        {active.map((overlap) => {
          const id = overlap.investigationId;
          const label = overlap.title || id || "";
          return (
            <Container key={overlap.investigationId}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `minmax(0, 1fr) ${ACTION_COLUMN_WIDTH}`,
                  gap: "16px",
                  alignItems: "start",
                }}
              >
                <SpaceBetween size="xs">
                  <Box variant="h3" fontWeight="bold">
                    <span style={{ overflowWrap: "anywhere" }}>{label}</span>
                  </Box>
                  <SpaceBetween
                    direction="horizontal"
                    size="xs"
                    alignItems="center"
                  >
                    <InvestigationStatusChip status={overlap.status} />
                    <Badge>{ownerLabel(overlap.owner)}</Badge>
                    <Badge color="blue">
                      {overlap.overlappingCount ?? 0} shared items
                    </Badge>
                    <Badge color="grey">
                      {relationLabel(overlap.relation)}
                    </Badge>
                  </SpaceBetween>
                </SpaceBetween>
                <SpaceBetween size="xs">
                  <OpenInvestigationButton
                    stage={stage}
                    investigationId={id}
                    title={label}
                    variant="normal"
                    fullWidth
                    ariaLabel={`Open investigation ${label} in a new tab`}
                  >
                    Open investigation
                  </OpenInvestigationButton>
                  {id && canBeChild(overlap) && (
                    <Button
                      fullWidth
                      onClick={() =>
                        onChoose({ kind: "child", parentInvestigationId: id })
                      }
                    >
                      Split out a narrower case
                    </Button>
                  )}
                </SpaceBetween>
              </div>
            </Container>
          );
        })}

        {history.length > 0 && (
          <Container
            header={
              <Header variant="h3">
                Previously resolved with an overlapping rule
              </Header>
            }
          >
            <SpaceBetween size="xs">
              {history.map((h) => (
                <TextContent key={h.investigationId}>
                  <p>
                    <strong>{h.title}</strong> – {h.owner} ·{" "}
                    {h.resolutionReason} · {formatTimestamp(h.resolvedAt)}
                    <br />
                    <small>{h.conclusion || "No conclusion recorded"}</small>
                  </p>
                </TextContent>
              ))}
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default InvestigationOverlapDialog;
